export const SOFTWARE_ROLES = Object.freeze({
  ENTRY: 'entry',
  INVENTORY_HEAD: 'inventory_head',
  PROCUREMENT: 'procurement',
  ISSUE_DEPT: 'issue_dept',
  ACCOUNTS_HEAD: 'accounts_head',
  ACCOUNT: 'account',
  CASH: 'cash',
  OPERATION_HEAD: 'operation_head',
  SERVICE_ENGINEER: 'service_eng',
  INSPECTIONS_DEPT: 'inspections_dept',
  HR: 'hr',
  ADMIN: 'admin',
  GUARD: 'guard',
  NONE: 'no_role',
});

export const SOFTWARE_ROLE_OPTIONS = Object.freeze([
  { value: SOFTWARE_ROLES.ENTRY, label: 'Entry' },
  { value: SOFTWARE_ROLES.INVENTORY_HEAD, label: 'Inventory Head' },
  { value: SOFTWARE_ROLES.PROCUREMENT, label: 'Procurement' },
  { value: SOFTWARE_ROLES.ISSUE_DEPT, label: 'Issue Dept' },
  { value: SOFTWARE_ROLES.ACCOUNTS_HEAD, label: 'Accounts Head' },
  { value: SOFTWARE_ROLES.ACCOUNT, label: 'Account' },
  { value: SOFTWARE_ROLES.CASH, label: 'Cash' },
  { value: SOFTWARE_ROLES.OPERATION_HEAD, label: 'Operation Head' },
  { value: SOFTWARE_ROLES.SERVICE_ENGINEER, label: 'Service Engineer' },
  { value: SOFTWARE_ROLES.INSPECTIONS_DEPT, label: 'Inspections Dept' },
  { value: SOFTWARE_ROLES.HR, label: 'HR' },
  { value: SOFTWARE_ROLES.ADMIN, label: 'Admin' },
  { value: SOFTWARE_ROLES.GUARD, label: 'Guard' },
  { value: SOFTWARE_ROLES.NONE, label: 'No Role' },
]);

const SOFTWARE_ROLE_LABELS = SOFTWARE_ROLE_OPTIONS.reduce((accumulator, option) => {
  accumulator[option.value] = option.label;
  return accumulator;
}, {});

const ROUTE_RULES = [
  { pattern: /^\/$/, roles: 'all' },
  { pattern: /^\/clients(?:\/|$)/, roles: [SOFTWARE_ROLES.ENTRY, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/vehicles(?:\/|$)/, roles: [SOFTWARE_ROLES.ENTRY, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/vendors(?:\/|$)/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/purchase(?:s(?:\/.*)?)?$/, roles: [SOFTWARE_ROLES.PROCUREMENT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/issued-parts(?:\/.*)?$/, roles: [SOFTWARE_ROLES.ISSUE_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/issue-parts(?:\/.*)?$/, roles: [SOFTWARE_ROLES.ISSUE_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/parts(?:\/.*)?$/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/tires(?:\/.*)?$/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/labor(?:\/.*)?$/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/canned-jobs(?:\/.*)?$/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory\/business-assets(?:\/.*)?$/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inventory(?:\/|$)/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/add-part(?:\/|$)/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/add-alternative-part(?:\/|$)/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/reports(?:\/|$)/, roles: [SOFTWARE_ROLES.INVENTORY_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/accounting(?:\/|$)/, roles: [SOFTWARE_ROLES.ACCOUNTS_HEAD, SOFTWARE_ROLES.ACCOUNT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/invoices(?:\/.*)?$/, roles: [SOFTWARE_ROLES.ACCOUNTS_HEAD, SOFTWARE_ROLES.CASH, SOFTWARE_ROLES.ACCOUNT, SOFTWARE_ROLES.ADMIN] },

  { pattern: /^\/estimates(?:\/.*)?$/, roles: [SOFTWARE_ROLES.ACCOUNTS_HEAD, SOFTWARE_ROLES.CASH, SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.ACCOUNT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/jobcards\/new(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/jobcards\/[^/]+\/edit(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.SERVICE_ENGINEER, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/jobcards(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.SERVICE_ENGINEER, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/bays(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/inspection(?:\/.*)?$/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.INSPECTIONS_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/appointments(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.INSPECTIONS_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/service-reminders(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.INSPECTIONS_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/time-tracking(?:\/|$)/, roles: [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.INSPECTIONS_DEPT, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/expenses(?:\/|$)/, roles: [SOFTWARE_ROLES.ACCOUNTS_HEAD, SOFTWARE_ROLES.ACCOUNT, SOFTWARE_ROLES.HR, SOFTWARE_ROLES.ADMIN] },
  { pattern: /^\/hr(?:\/|$)/, roles: [SOFTWARE_ROLES.HR, SOFTWARE_ROLES.ADMIN] },
];

export const normalizeSoftwareRole = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return SOFTWARE_ROLE_LABELS[normalized] ? normalized : SOFTWARE_ROLES.NONE;
};

export const getSoftwareRoleLabel = (value) => SOFTWARE_ROLE_LABELS[normalizeSoftwareRole(value)] || SOFTWARE_ROLE_LABELS[SOFTWARE_ROLES.NONE];

const inferSoftwareRole = (user) => {
  const explicitRole = normalizeSoftwareRole(user?.softwareRole);
  if (explicitRole !== SOFTWARE_ROLES.NONE) {
    return explicitRole;
  }

  const legacyRole = String(user?.role || '').trim().toLowerCase();
  if (legacyRole === 'admin') {
    return SOFTWARE_ROLES.ADMIN;
  }
  if (legacyRole === 'hr') {
    return SOFTWARE_ROLES.HR;
  }

  const email = String(user?.email || '').trim().toLowerCase();
  if (email === 'demo@example.com') {
    return SOFTWARE_ROLES.ADMIN;
  }

  return SOFTWARE_ROLES.NONE;
};

const decodeTokenPayload = (token) => {
  try {
    const payload = String(token || '').split('.')[1];
    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = `${base64}${'='.repeat((4 - (base64.length % 4 || 4)) % 4)}`;
    return JSON.parse(window.atob(padded));
  } catch (error) {
    return null;
  }
};

export const getCurrentAppUser = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      return {
        ...parsed,
        softwareRole: inferSoftwareRole(parsed),
      };
    }
  } catch (error) {
    // Ignore invalid local storage and fall back to token payload.
  }

  const payload = decodeTokenPayload(localStorage.getItem('token'));
  return payload
    ? {
      ...payload,
      softwareRole: inferSoftwareRole(payload),
    }
    : null;
};

export const canAccessPath = (path, user = getCurrentAppUser()) => {
  const normalizedPath = String(path || '/').trim() || '/';
  const softwareRole = normalizeSoftwareRole(user?.softwareRole);

  if (softwareRole === SOFTWARE_ROLES.ADMIN) {
    return true;
  }

  const rule = ROUTE_RULES.find((candidate) => candidate.pattern.test(normalizedPath));
  if (!rule) {
    return normalizedPath === '/';
  }

  if (rule.roles === 'all') {
    return true;
  }

  return rule.roles.includes(softwareRole);
};

export const filterItemsByAccess = (items, pathField = 'path', user = getCurrentAppUser()) => (
  (items || []).filter((item) => {
    if (!item?.[pathField]) {
      return normalizeSoftwareRole(user?.softwareRole) === SOFTWARE_ROLES.ADMIN;
    }

    return canAccessPath(item[pathField], user);
  })
);
